// Shared grouping for the customer browse surfaces (public store + in-app order screen).
//
// A product can belong to multiple categories (its `categoryField` is a list of names,
// e.g. ["Populars", "Chicken"]). We push each product into EVERY one of its categories
// so it shows up under each. Products are assumed to already be in the vendor's chosen
// sort order (the API orders by f_sort_order), so within-category order is preserved.
//
// Category display order follows `orderedNames` (the vendor's saved order) first, then
// any remaining categories in first-appearance order.

const UNCATEGORIZED = 'Other';

/**
 * Return a product's list of category names, falling back to the legacy single field.
 */
export function productCategoryList(p, arrayField = 'f_categories', legacyField = 'f_business_product_category') {
    const arr = p && p[arrayField];
    if (Array.isArray(arr) && arr.length) return arr;
    const single = p && p[legacyField];
    return single ? [single] : [];
}

/**
 * @param {Array} products      list of product objects (already in sort order)
 * @param {string} categoryField name of the array field holding category names (e.g. 'f_categories')
 * @param {string} legacyField   single-string fallback field for older rows (e.g. 'f_category')
 * @param {Array<string>} orderedNames  vendor's saved category order
 * @returns {{ grouped: Object, categories: string[] }}
 */
export function groupByCategory(products, categoryField, legacyField, orderedNames = []) {
    const grouped = {};
    const appearance = [];

    (products || []).forEach((p) => {
        let cats = p[categoryField];
        if (!Array.isArray(cats) || cats.length === 0) {
            const single = legacyField ? p[legacyField] : null;
            cats = single ? [single] : [UNCATEGORIZED];
        }
        cats.forEach((cat) => {
            const key = cat || UNCATEGORIZED;
            if (!grouped[key]) {
                grouped[key] = [];
                appearance.push(key);
            }
            grouped[key].push(p);
        });
    });

    // Order: saved order first (only categories that actually have products), then leftovers.
    const seen = new Set();
    const categories = [];
    (orderedNames || []).forEach((name) => {
        if (grouped[name] && !seen.has(name)) {
            seen.add(name);
            categories.push(name);
        }
    });
    appearance.forEach((name) => {
        if (!seen.has(name)) {
            seen.add(name);
            categories.push(name);
        }
    });

    return { grouped, categories };
}

/**
 * Merge the categories that exist on products with a saved order list, returning the
 * full ordered list of distinct category names (saved order first, then new ones).
 * Used by the vendor's category manager.
 */
export function mergeCategoryOrder(existingNames, orderedNames = []) {
    const existing = new Set(existingNames);
    const seen = new Set();
    const result = [];
    (orderedNames || []).forEach((name) => {
        if (existing.has(name) && !seen.has(name)) {
            seen.add(name);
            result.push(name);
        }
    });
    existingNames.forEach((name) => {
        if (!seen.has(name)) {
            seen.add(name);
            result.push(name);
        }
    });
    return result;
}
